import React from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@material-ui/core';
import { Button } from 'm2m.components';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { TransferenciaLoteResponse, TransferenciaLoteItem, TransferenciaLoteStatus, TransferenciaDocumentoLoteResponse } from 'common/genericTypes';
import { addCpfCnpjMask } from 'utils/mask';
import { formatIntNumber } from 'utils/number';

interface Props {
  open: boolean;
  onClose: () => void;
  data: TransferenciaLoteResponse;
  documentos?: TransferenciaDocumentoLoteResponse | null;
}

const statusConfig: Record<TransferenciaLoteStatus, { icon: React.ReactNode; color: string }> = {
  SUCESSO: {
    icon: <CheckCircleOutlineIcon style={{ fontSize: 40, color: '#4caf50' }} />,
    color: '#4caf50',
  },
  PARCIAL: {
    icon: <WarningIcon style={{ fontSize: 40, color: '#ff9800' }} />,
    color: '#ff9800',
  },
  FALHA: {
    icon: <ErrorOutlineIcon style={{ fontSize: 40, color: '#f44336' }} />,
    color: '#f44336',
  },
};

const tituloByStatus: Record<TransferenciaLoteStatus, string> = {
  SUCESSO: 'Transferência processada com sucesso',
  PARCIAL: 'Transferência processada parcialmente',
  FALHA: 'Transferência falhou',
};

const agruparItensFalha = (itens: TransferenciaLoteItem[]) => {
  const falhas = itens.filter((i) => !i.sucesso);
  const mapa = new Map<string, TransferenciaLoteItem[]>();
  falhas.forEach((item) => {
    const key = item.codigoIF;
    if (!mapa.has(key)) mapa.set(key, []);
    mapa.get(key)!.push(item);
  });
  return Array.from(mapa.entries()).map(([codigo, destinos]) => ({ codigo, destinos }));
};

const TransferenciaFeedbackModal: React.FC<Props> = ({ open, onClose, data, documentos }) => {
  const config = statusConfig[data.status];
  const ifsComFalha = agruparItensFalha(data.itens);
  const temFalhaDocumento = documentos?.itens.some((i) => !i.sucesso) ?? false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={ifsComFalha.length === 0 && !temFalhaDocumento ? 'sm' : 'md'} fullWidth>
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
          {config.icon}
          <Typography variant="h6" style={{ color: config.color }}>
            {tituloByStatus[data.status]}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Box display="flex" style={{ gap: '24px', marginBottom: '20px' }}>
          {[
            { label: 'Total', value: data.resumo.totalProcessados },
            { label: 'Sucesso', value: data.resumo.totalSucesso, color: '#4caf50' },
            { label: 'Falha', value: data.resumo.totalFalha, color: '#f44336' },
          ].map((card) => (
            <Box
              key={card.label}
              p={1.5}
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                minWidth: '80px',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {card.label}
              </Typography>
              <Typography variant="h5" style={{ color: card.color }}>
                <strong>{card.value}</strong>
              </Typography>
            </Box>
          ))}
        </Box>

        {ifsComFalha.length > 0 && (
          <>
            <Divider style={{ marginBottom: '16px' }} />
            <Typography variant="subtitle1" style={{ marginBottom: '12px' }}>
              <strong>{'Falhas na transferência'}</strong>
            </Typography>
            {ifsComFalha.map((ifErro, ifIndex) => (
              <Box key={ifErro.codigo} mb={ifIndex < ifsComFalha.length - 1 ? 3 : 0}>
                <Box
                  p={1.5}
                  style={{ backgroundColor: '#fafafa', borderRadius: '4px', border: '1px solid #e0e0e0' }}
                >
                  <Typography variant="subtitle1">
                    <strong>{`IF: ${ifErro.codigo}`}</strong>
                  </Typography>
                </Box>

                {ifErro.destinos.map((item, dIndex) => (
                  <Box
                    key={dIndex}
                    mt={1}
                    ml={2}
                    p={1.5}
                    style={{
                      border: '1px solid #ffcdd2',
                      borderRadius: '4px',
                      backgroundColor: '#fff5f5',
                    }}
                  >
                    <Box display="flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
                      {item.custodianteDestinoCnpj && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            {'Custodiante'}
                          </Typography>
                          <Typography variant="body2">
                            {addCpfCnpjMask(item.custodianteDestinoCnpj)}
                          </Typography>
                        </Box>
                      )}
                      {item.investidorDestinoCpfCnpj && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            {'Investidor'}
                          </Typography>
                          <Typography variant="body2">
                            {addCpfCnpjMask(item.investidorDestinoCpfCnpj)}
                          </Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {'Quantidade'}
                        </Typography>
                        <Typography variant="body2">{formatIntNumber(item.quantidade)}</Typography>
                      </Box>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="body2" color="error">
                        {item.mensagemErro}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </>
        )}

        {temFalhaDocumento && documentos && (
          <>
            <Box mt={3}>
              <Divider style={{ marginBottom: '16px' }} />
            </Box>
            <Typography variant="subtitle1" style={{ marginBottom: '12px' }}>
              <strong>{'Falhas no envio de documentos'}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
              {'A transferência foi realizada com sucesso, porém os documentos não foram enviados para os itens abaixo:'}
            </Typography>
            {documentos.itens
              .filter((i) => !i.sucesso)
              .map((item, idx) => (
                <Box
                  key={idx}
                  mb={1}
                  p={1.5}
                  style={{
                    border: '1px solid #ffe0b2',
                    borderRadius: '4px',
                    backgroundColor: '#fff8e1',
                  }}
                >
                  <Box display="flex" style={{ gap: '24px' }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {'Operação'}
                      </Typography>
                      <Typography variant="body2">{item.operacaoId}</Typography>
                    </Box>
                  </Box>
                  <Box mt={1}>
                    <Typography variant="body2" color="error">
                      {item.erro?.mensagem ?? 'Erro desconhecido'}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          {'Fechar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferenciaFeedbackModal;
